import { createAsyncThunk } from "@reduxjs/toolkit";
import { ENDPOINTS } from "../../../../Api/Endpoints";
import { apiRequest } from "../../../../Api/Api";
import { tokenManager } from "../../../../Api/tokenManager";
import { validateEmailInput } from "../../../../utils/emailValidation";

const normalizeMobile = (value) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);

const toEstablishedYear = (value) => {
  const year = Number(value);
  const nowYear = new Date().getFullYear();
  if (!Number.isInteger(year)) return null;
  if (year >= 1800 && year <= nowYear) return year;
  if (year >= 0 && year <= 120) return nowYear - year;
  return null;
};

const getContentType = (file) => {
  if (file?.type) return file.type;

  const name = String(file?.name || "").toLowerCase();

  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";

  return "application/octet-stream";
};

// Use decimal MB (1 MB = 1,000,000 bytes) so "under 10 MB" matches Windows/Explorer display
const validateFile = (file, label, allowedTypes, maxSizeMb = 10) => {
  if (!file) {
    throw new Error(`${label} is required`);
  }

  const fileType = getContentType(file);

  if (!allowedTypes.includes(fileType)) {
    throw new Error(`${label} format is invalid`);
  }

  const maxSizeBytes = maxSizeMb * 1000 * 1000;
  if (file.size > maxSizeBytes) {
    throw new Error(`${label} must be under ${maxSizeMb} MB`);
  }
};

const uploadToSignedUrl = async ({ uploadUrl, file }) => {
  let response;
  try {
    response = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": getContentType(file),
      },
      body: file,
    });
  } catch (e) {
    const msg = e?.message || "";
    if (msg === "Failed to fetch" || msg === "Network Error") {
      throw new Error(
        "Document upload failed. Check your internet connection and that the backend is running."
      );
    }
    throw e;
  }

  if (!response.ok) {
    throw new Error(`File upload failed (${response.status})`);
  }

  return true;
};

export const signupDoctor = createAsyncThunk(
  "doctorAuth/signupDoctor",
  async (payload, { rejectWithValue }) => {
    try {
      const isHospitalFlow = Boolean(payload?.isHospitalFlow);
      const signupUploadUrl = isHospitalFlow
        ? ENDPOINTS.HOSPITAL_SIGNUP_UPLOAD_URLS
        : ENDPOINTS.DOCTOR_SIGNUP_UPLOAD_URLS;
      const signupUrl = isHospitalFlow
        ? ENDPOINTS.HOSPITAL_SIGNUP
        : ENDPOINTS.DOCTOR_SIGNUP;
      const mobile = normalizeMobile(payload?.mobile);
      const fullName = String(payload?.fullName || "").trim();
      const city = String(payload?.city || "").trim();
      const state = String(payload?.state || "").trim();
      const specialization = String(payload?.specialization || "").trim();
      const experience = String(payload?.experience || "").trim();
      const regId = String(payload?.regId || "").trim();
      const hospital = String(payload?.hospital || "").trim();
      const clinicOrHospitalName = hospital || "Independent Practice";
      const hospitalType = String(payload?.hospitalType || specialization || "").trim();
      const establishedYear = String(payload?.establishedYear || experience || "").trim();
      const licenseNumber = String(payload?.licenseNumber || regId || "").trim();
      const emergencyContactNumber = normalizeMobile(payload?.emergencyContactNumber);
      const emailRaw = String(payload?.email || "").trim();
      const emailVal = validateEmailInput(emailRaw);
      if (!emailVal.valid) {
        throw new Error(emailVal.message);
      }
      const email = emailVal.cleaned;
      const pincode = String(payload?.pincode || "").trim();
      const selectedCategoryIds = Array.isArray(payload?.selectedCategoryIds)
        ? payload.selectedCategoryIds.map((id) => String(id)).filter(Boolean)
        : [];
      const selectedSubCategoryIds = Array.isArray(payload?.selectedSubCategoryIds)
        ? payload.selectedSubCategoryIds.map((id) => String(id)).filter(Boolean)
        : [];
      const selectedSelections = Array.isArray(payload?.selectedSelections)
        ? payload.selectedSelections
        : [];
      const categorySubcategoryTree = Array.isArray(payload?.categorySubcategoryTree)
        ? payload.categorySubcategoryTree
        : [];
      const degrees = Array.isArray(payload?.degrees)
        ? payload.degrees
            .map((d) => ({
              title: String(d?.title || "").trim(),
              institution: String(d?.institution || "").trim(),
              year: String(d?.year || "").trim() || undefined,
            }))
            .filter((d) => d.title)
        : [];

      if (fullName.length < 2) {
        throw new Error("Enter valid full name");
      }

      if (!mobile || mobile.length !== 10) {
        throw new Error("Enter a valid 10 digit mobile number");
      }

      if (city.length < 2) {
        throw new Error("Enter valid city");
      }

      if (state.length < 2) {
        throw new Error("Enter valid state");
      }

      if (isHospitalFlow) {
        if (hospitalType.length < 2) {
          throw new Error("Enter valid hospital type");
        }
        const parsedEstablishedYear = toEstablishedYear(establishedYear);
        if (parsedEstablishedYear == null) {
          throw new Error("Enter valid established year (e.g. 2012 or years running)");
        }
        if (licenseNumber.length < 4) {
          throw new Error("Enter valid hospital license / registration number");
        }
        if (!emergencyContactNumber || emergencyContactNumber.length !== 10) {
          throw new Error("Enter a valid 10 digit emergency contact number");
        }
      } else {
        if (specialization.length < 2) {
          throw new Error("Enter valid specialization");
        }

        if (!experience) {
          throw new Error("Enter years of experience");
        }

        if (regId.length < 4) {
          throw new Error("Enter valid registration ID");
        }

        if (degrees.length === 0) {
          throw new Error("Add at least one medical degree (e.g. MBBS, MD)");
        }

      }

      validateFile(
        payload?.imageFile,
        "Profile picture",
        ["image/png", "image/jpeg"],
        5
      );

      validateFile(
        payload?.degreeFile,
        "Medical degree",
        ["application/pdf", "image/png", "image/jpeg"],
        15
      );

      validateFile(
        payload?.regFile,
        "Registration certificate",
        ["application/pdf", "image/png", "image/jpeg"],
        15
      );

      const uploadUrlRes = await apiRequest({
        method: "POST",
        url: signupUploadUrl,
        data: {
          imageFilename: payload.imageFile.name,
          medicalDegreeFilename: payload.degreeFile.name,
          registrationCertificateFilename: payload.regFile.name,
        },
      });

      const uploadData = uploadUrlRes?.data || uploadUrlRes || {};

      const imageUpload = uploadData?.image;
      const medicalDegreeUpload = uploadData?.medicalDegree;
      const registrationCertificateUpload = uploadData?.registrationCertificate;

      if (
        !imageUpload?.uploadUrl ||
        !imageUpload?.key ||
        !medicalDegreeUpload?.uploadUrl ||
        !medicalDegreeUpload?.key ||
        !registrationCertificateUpload?.uploadUrl ||
        !registrationCertificateUpload?.key
      ) {
        throw new Error("Upload URL response is incomplete");
      }

      const submitMultipartFallback = async () => {
        const form = new FormData();
        form.append("mobile", mobile);
        form.append("name", fullName);
        form.append("email", email);
        form.append("cityOfPractice", city);
        form.append("state", state);
        form.append("pincode", pincode);
        if (isHospitalFlow) form.append("emergencyContactNumber", emergencyContactNumber);
        if (payload?.latitude != null) form.append("latitude", String(payload.latitude));
        if (payload?.longitude != null) form.append("longitude", String(payload.longitude));
        form.append("specialization", isHospitalFlow ? hospitalType : specialization);
        const parsedEstablishedYear = toEstablishedYear(establishedYear);
        const yearsRunning = parsedEstablishedYear == null ? "" : String(Math.max(0, new Date().getFullYear() - parsedEstablishedYear));
        form.append("yearsOfExperience", isHospitalFlow ? yearsRunning : experience);
        form.append("registrationId", isHospitalFlow ? licenseNumber : regId);
        if (!isHospitalFlow && degrees.length) {
          form.append("degrees", JSON.stringify(degrees));
          form.append("degree", degrees.map((d) => d.title).join(", "));
        }
        form.append("hospitalClinicName", isHospitalFlow ? fullName : clinicOrHospitalName);
        if (isHospitalFlow) {
          form.append("hospitalType", hospitalType);
          form.append("establishedYear", String(parsedEstablishedYear ?? establishedYear));
          form.append("licenseNumber", licenseNumber);
        }
        form.append("selectedCategoryIds", JSON.stringify(selectedCategoryIds));
        form.append("selectedSubCategoryIds", JSON.stringify(selectedSubCategoryIds));
        form.append("selectedSelections", JSON.stringify(selectedSelections));
        form.append("categorySubcategoryTree", JSON.stringify(categorySubcategoryTree));
        form.append("mode", payload?.mode || "inClinic");
        const doctorSlotBlocks = Array.isArray(payload?.doctorSlotBlocks) ? payload.doctorSlotBlocks : [];
        const activeDaysFromSlots = [
          ...new Set(doctorSlotBlocks.map((r) => r?.day).filter(Boolean)),
        ];
        form.append(
          "activeDays",
          JSON.stringify(!isHospitalFlow ? activeDaysFromSlots : payload?.activeDays || [])
        );
        form.append("preferredText", payload?.preferredText || "");
        form.append("doctorSlotBlocks", JSON.stringify(doctorSlotBlocks));
        if (isHospitalFlow) {
          form.append("emergency24x7", String(Boolean(payload?.emergency24x7)));
          form.append(
            "departmentOpdSchedules",
            JSON.stringify(Array.isArray(payload?.departmentOpdSchedules) ? payload.departmentOpdSchedules : [])
          );
        }

        form.append("image", payload.imageFile);
        form.append("medicalDegree", payload.degreeFile);
        form.append("registrationCertificate", payload.regFile);

        const signupRes = await apiRequest({
          method: "POST",
          url: signupUrl,
          data: form,
        });

        return {
          mobile,
          signupResponse: signupRes?.data || signupRes,
          uploadedKeys: null,
          usedFallback: true,
        };
      };

      try {
        await Promise.all([
          uploadToSignedUrl({
            uploadUrl: imageUpload.uploadUrl,
            file: payload.imageFile,
          }),
          uploadToSignedUrl({
            uploadUrl: medicalDegreeUpload.uploadUrl,
            file: payload.degreeFile,
          }),
          uploadToSignedUrl({
            uploadUrl: registrationCertificateUpload.uploadUrl,
            file: payload.regFile,
          }),
        ]);
      } catch (uploadError) {
        // If S3 CORS/presigned upload fails (common in dev), fall back to multipart upload to backend.
        // Backend accepts multipart/form-data for /doctor/signup via multer.
        try {
          return await submitMultipartFallback();
        } catch (fallbackErr) {
          throw new Error(
            fallbackErr?.message ||
              uploadError?.message ||
              "Document upload failed. Check CORS or signed URL configuration."
          );
        }
      }

      const signupRes = await apiRequest({
        method: "POST",
        url: signupUrl,
        data: {
          mobile,
          name: fullName,
          email,
          cityOfPractice: city,
          state,
          pincode,
          emergencyContactNumber: isHospitalFlow ? emergencyContactNumber : undefined,
          latitude: payload?.latitude != null ? Number(payload.latitude) : undefined,
          longitude: payload?.longitude != null ? Number(payload.longitude) : undefined,
          specialization: isHospitalFlow ? hospitalType : specialization,
          yearsOfExperience: isHospitalFlow
            ? Math.max(0, new Date().getFullYear() - Number(toEstablishedYear(establishedYear)))
            : experience,
          registrationId: isHospitalFlow ? licenseNumber : regId,
          degrees: !isHospitalFlow ? degrees : undefined,
          degree: !isHospitalFlow ? degrees.map((d) => d.title).join(", ") : undefined,
          hospitalClinicName: isHospitalFlow ? fullName : clinicOrHospitalName,
          hospitalType: isHospitalFlow ? hospitalType : undefined,
          establishedYear: isHospitalFlow ? toEstablishedYear(establishedYear) : undefined,
          licenseNumber: isHospitalFlow ? licenseNumber : undefined,
          selectedCategoryIds,
          selectedSubCategoryIds,
          selectedSelections,
          categorySubcategoryTree,
          imageKey: imageUpload.key,
          medicalDegreeKey: medicalDegreeUpload.key,
          registrationCertificateKey: registrationCertificateUpload.key,
          mode: payload?.mode || "inClinic",
          activeDays: !isHospitalFlow
            ? [
                ...new Set(
                  (Array.isArray(payload?.doctorSlotBlocks) ? payload.doctorSlotBlocks : []).map(
                    (r) => r?.day
                  ).filter(Boolean)
                ),
              ]
            : payload?.activeDays || [],
          preferredText: payload?.preferredText || "",
          doctorSlotBlocks: !isHospitalFlow ? payload?.doctorSlotBlocks || [] : undefined,
          emergency24x7: isHospitalFlow ? Boolean(payload?.emergency24x7) : undefined,
          departmentOpdSchedules: isHospitalFlow ? payload?.departmentOpdSchedules || [] : undefined,
        },
      });

      return {
        mobile,
        signupResponse: signupRes?.data || signupRes,
        uploadedKeys: {
          imageKey: imageUpload.key,
          medicalDegreeKey: medicalDegreeUpload.key,
          registrationCertificateKey: registrationCertificateUpload.key,
        },
      };
    } catch (error) {
      const raw =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Doctor signup failed";
      const isNetworkFailure =
        raw === "Failed to fetch" || raw === "Network Error";
      const message = isNetworkFailure
        ? "Cannot reach the server. Make sure the backend is running (e.g. npm start in HealBid_Backend) and REACT_APP_API_BASE_URL is correct."
        : raw;
      return rejectWithValue(message);
    }
  }
);

export const verifyDoctorOtp = createAsyncThunk(
  "doctorAuth/verifyDoctorOtp",
  async ({ mobile, otp, isHospitalFlow = false }, { rejectWithValue }) => {
    try {
      const normalizedMobile = normalizeMobile(mobile);

      const cleanOtp = String(otp || "")
        .replace(/\D/g, "")
        .slice(0, 4);

      if (!normalizedMobile || normalizedMobile.length !== 10) {
        throw new Error("Mobile number missing");
      }

      if (cleanOtp.length !== 4) {
        throw new Error("Enter valid OTP");
      }

      const verifyUrl = isHospitalFlow
        ? ENDPOINTS.HOSPITAL_VERIFY_OTP
        : ENDPOINTS.DOCTOR_VERIFY_OTP;

      const res = await apiRequest({
        method: "POST",
        url: verifyUrl,
        data: {
          mobile: normalizedMobile,
          otp: cleanOtp,
        },
      });

      const data = res?.data || res || {};

      const accessToken =
        data?.accessToken ||
        data?.token ||
        data?.data?.accessToken ||
        data?.data?.token ||
        null;

      const refreshToken =
        data?.refreshToken ||
        data?.data?.refreshToken ||
        null;

      if (accessToken) {
        const role = isHospitalFlow ? "hospital" : "doctor";
        tokenManager.setTokensForRole(role, accessToken, refreshToken);
        tokenManager.setActiveProviderRole(role);
      }

      return {
        success: true,
        hasToken: Boolean(accessToken),
        accessToken,
        refreshToken,
        mobile: normalizedMobile,
        data,
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "OTP verification failed"
      );
    }
  }
);

export const sendProviderEmailOtp = createAsyncThunk(
  "doctorAuth/sendProviderEmailOtp",
  async ({ mobile, email, isHospitalFlow = false }, { rejectWithValue }) => {
    try {
      const normalizedMobile = normalizeMobile(mobile);
      const endpoint = isHospitalFlow
        ? ENDPOINTS.HOSPITAL_SEND_EMAIL_OTP
        : ENDPOINTS.DOCTOR_SEND_EMAIL_OTP;
      const data = await apiRequest({
        method: "POST",
        url: endpoint,
        data: { mobile: normalizedMobile, email: String(email || "").trim() },
      });
      return data?.data || data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to send email OTP"
      );
    }
  }
);

export const resendProviderEmailOtp = createAsyncThunk(
  "doctorAuth/resendProviderEmailOtp",
  async ({ mobile, email, isHospitalFlow = false }, { rejectWithValue }) => {
    try {
      const normalizedMobile = normalizeMobile(mobile);
      const endpoint = isHospitalFlow
        ? ENDPOINTS.HOSPITAL_RESEND_EMAIL_OTP
        : ENDPOINTS.DOCTOR_RESEND_EMAIL_OTP;
      const data = await apiRequest({
        method: "POST",
        url: endpoint,
        data: { mobile: normalizedMobile, email: String(email || "").trim() },
      });
      return data?.data || data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to resend email OTP"
      );
    }
  }
);

export const verifyProviderEmailOtp = createAsyncThunk(
  "doctorAuth/verifyProviderEmailOtp",
  async ({ mobile, email, otp, isHospitalFlow = false }, { rejectWithValue }) => {
    try {
      const normalizedMobile = normalizeMobile(mobile);
      const cleanOtp = String(otp || "")
        .replace(/\D/g, "")
        .slice(0, 4);
      const endpoint = isHospitalFlow
        ? ENDPOINTS.HOSPITAL_VERIFY_EMAIL_OTP
        : ENDPOINTS.DOCTOR_VERIFY_EMAIL_OTP;
      const data = await apiRequest({
        method: "POST",
        url: endpoint,
        data: { mobile: normalizedMobile, email: String(email || "").trim(), otp: cleanOtp },
      });
      return data?.data || data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Email OTP verification failed"
      );
    }
  }
);
